import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  BookOpen, Award, Trophy, Clock, CheckCircle2, ChevronRight,
  Play, Target, BarChart3, Shield, Users, ArrowLeft, ArrowRight,
  AlertTriangle, Star, Zap, FileText, Eye, EyeOff
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import api from '@/services/api';
import { useTenant } from '@/contexts/TenantContext';
import { PdfSlideViewer, PdfUploadControl } from '@/components/PdfSlideViewer';

const CATEGORY_LABELS = {
  threat_awareness: 'Threat Awareness',
  access_control: 'Access Control',
  data_protection: 'Data Protection',
  incident_management: 'Incident Management',
  secure_development: 'Secure Development',
  cloud_security: 'Cloud Security',
  ai_security: 'AI Security',
  vendor_risk: 'Vendor Risk',
  physical_security: 'Physical Security',
  regulatory: 'Regulatory',
  industry_specific: 'Industry Specific',
  ot_security: 'OT/ICS Security',
};

const CATEGORY_COLORS = {
  threat_awareness: 'bg-red-100 text-red-700 border-red-200',
  access_control: 'bg-blue-100 text-blue-700 border-blue-200',
  data_protection: 'bg-purple-100 text-purple-700 border-purple-200',
  incident_management: 'bg-amber-100 text-amber-700 border-amber-200',
  secure_development: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  cloud_security: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  ai_security: 'bg-violet-100 text-violet-700 border-violet-200',
  vendor_risk: 'bg-orange-100 text-orange-700 border-orange-200',
  physical_security: 'bg-slate-100 text-slate-700 border-slate-200',
  regulatory: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  industry_specific: 'bg-teal-100 text-teal-700 border-teal-200',
  ot_security: 'bg-rose-100 text-rose-700 border-rose-200',
};

const DIFF_COLORS = { beginner: 'bg-green-100 text-green-700', intermediate: 'bg-amber-100 text-amber-700', advanced: 'bg-red-100 text-red-700' };

// ─── Training Dashboard ─────────────────────────────────────────
export const TrainingDashboard = () => {
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [progress, setProgress] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('modules');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [modRes, progRes] = await Promise.all([
        api.get('/training/modules'),
        api.get('/training/my-progress'),
      ]);
      setModules(modRes.data.modules || []);
      setProgress(progRes.data);
    } catch (err) {
      toast.error('Failed to load training data');
    } finally { setLoading(false); }
  };

  const loadLeaderboard = async () => {
    try {
      const res = await api.get('/training/leaderboard');
      setLeaderboard(res.data.leaderboard || []);
    } catch { /* ignore */ }
  };

  const getModuleStatus = (code) => {
    if (!progress?.assignments) return 'not_started';
    const a = progress.assignments.find(a => a.module_code === code);
    return a?.status || 'not_started';
  };

  const getModuleScore = (code) => {
    const a = progress?.assignments?.find(a => a.module_code === code);
    return a?.quiz_score;
  };

  if (loading) return <div className="p-6"><div className="animate-pulse space-y-4"><div className="h-8 bg-gray-200 rounded w-1/3"></div><div className="grid grid-cols-4 gap-4">{[1,2,3,4].map(i=><div key={i} className="h-24 bg-gray-200 rounded"></div>)}</div></div></div>;

  return (
    <div className="p-6 space-y-6" data-testid="training-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><BookOpen className="w-7 h-7 text-indigo-600" />Security Awareness Training</h1>
          <p className="text-gray-500 mt-1">Build your security knowledge with interactive modules and quizzes</p>
        </div>
      </div>

      {/* Progress Summary */}
      {progress && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Completed', val: progress.completed, total: progress.total_modules, icon: <CheckCircle2 className="w-4 h-4 text-emerald-600" />, color: 'border-l-emerald-400' },
            { label: 'In Progress', val: progress.in_progress, icon: <Play className="w-4 h-4 text-blue-600" />, color: 'border-l-blue-400' },
            { label: 'Not Started', val: progress.not_started, icon: <Clock className="w-4 h-4 text-slate-500" />, color: 'border-l-slate-300' },
            { label: 'Avg Score', val: `${progress.average_score}%`, icon: <Target className="w-4 h-4 text-violet-600" />, color: 'border-l-violet-400' },
            { label: 'Badges', val: progress.badges?.length || 0, icon: <Award className="w-4 h-4 text-amber-500" />, color: 'border-l-amber-400' },
          ].map((m, i) => (
            <Card key={i} className={`border-l-4 ${m.color}`}>
              <CardContent className="pt-3 pb-3">
                <div className="flex items-center gap-2">{m.icon}<div><p className="text-xl font-bold" data-testid={`training-stat-${i}`}>{m.val}</p><p className="text-xs text-muted-foreground">{m.label}</p></div></div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {progress && (
        <div>
          <div className="flex items-center gap-3 mb-1">
            <span className="text-sm font-medium">Overall Progress</span>
            <span className="text-sm text-muted-foreground">{progress.completion_pct}%</span>
          </div>
          <Progress value={progress.completion_pct} className="h-2" />
        </div>
      )}

      {/* Badges */}
      {progress?.badges?.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {progress.badges.map((b, i) => (
            <Badge key={i} variant="outline" className="gap-1 px-3 py-1.5 border-amber-300 bg-amber-50 text-amber-700" data-testid={`badge-${b.badge_type}`}>
              <Star className="w-3 h-3" />{b.badge_name}
            </Badge>
          ))}
        </div>
      )}

      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); if (v === 'leaderboard') loadLeaderboard(); }}>
        <TabsList>
          <TabsTrigger value="modules">Modules ({modules.length})</TabsTrigger>
          <TabsTrigger value="leaderboard" data-testid="leaderboard-tab">Leaderboard</TabsTrigger>
          <TabsTrigger value="compliance" data-testid="compliance-tab">Compliance Matrix</TabsTrigger>
        </TabsList>

        <TabsContent value="modules" className="space-y-3 mt-4">
          {/* Onboarding modules first */}
          {progress?.onboarding_modules && (
            <div className="mb-2">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">Mandatory Onboarding</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {modules.filter(m => m.is_mandatory_onboarding).map(m => <ModuleCard key={m.code} module={m} status={getModuleStatus(m.code)} score={getModuleScore(m.code)} navigate={navigate} />)}
              </div>
            </div>
          )}
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mt-4 mb-2">All Courses</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {modules.filter(m => !m.is_mandatory_onboarding).map(m => <ModuleCard key={m.code} module={m} status={getModuleStatus(m.code)} score={getModuleScore(m.code)} navigate={navigate} />)}
          </div>
        </TabsContent>

        <TabsContent value="leaderboard" className="mt-4">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-500" />Training Leaderboard</CardTitle></CardHeader>
            <CardContent>
              {leaderboard.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">No completions yet. Be the first!</p>
              ) : (
                <div className="space-y-2">
                  {leaderboard.map((entry) => (
                    <div key={entry.user_id} className={`flex items-center justify-between p-3 rounded-lg ${entry.rank <= 3 ? 'bg-amber-50 border border-amber-100' : 'bg-gray-50'}`} data-testid={`leaderboard-${entry.rank}`}>
                      <div className="flex items-center gap-3">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${entry.rank === 1 ? 'bg-amber-400 text-white' : entry.rank === 2 ? 'bg-gray-300 text-gray-700' : entry.rank === 3 ? 'bg-orange-300 text-orange-800' : 'bg-gray-200 text-gray-600'}`}>{entry.rank}</span>
                        <div><p className="font-medium text-sm">{entry.name}</p><p className="text-xs text-muted-foreground">{entry.email}</p></div>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <div className="text-center"><p className="font-bold">{entry.completed_count}</p><p className="text-xs text-muted-foreground">Completed</p></div>
                        <div className="text-center"><p className="font-bold">{entry.avg_score}%</p><p className="text-xs text-muted-foreground">Avg Score</p></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="compliance" className="mt-4">
          <ComplianceMatrix />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ─── Module Card ──────────────────────────────────────────────
const ModuleCard = ({ module, status, score, navigate }) => {
  const statusConfig = {
    completed: { label: 'Completed', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: <CheckCircle2 className="w-3 h-3" /> },
    in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: <Play className="w-3 h-3" /> },
    not_started: { label: 'Not Started', color: 'bg-gray-100 text-gray-600 border-gray-200', icon: <Clock className="w-3 h-3" /> },
  };
  const s = statusConfig[status] || statusConfig.not_started;

  return (
    <Card className="hover:shadow-md transition-shadow cursor-pointer group" onClick={() => navigate(`/grc/training/${module.code}`)} data-testid={`module-card-${module.code}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={`text-xs ${CATEGORY_COLORS[module.category] || 'bg-gray-100'}`}>{CATEGORY_LABELS[module.category] || module.category}</Badge>
            <Badge variant="outline" className={`text-xs ${DIFF_COLORS[module.difficulty]}`}>{module.difficulty}</Badge>
          </div>
          <Badge variant="outline" className={`text-xs gap-1 ${s.color}`}>{s.icon}{s.label}</Badge>
        </div>
        <h3 className="font-semibold text-sm mb-1 group-hover:text-indigo-600 transition-colors">{module.title}</h3>
        <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{module.description}</p>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{module.duration_minutes}min</span>
          <span className="flex items-center gap-1"><FileText className="w-3 h-3" />{module.slides?.length || 0} slides</span>
          <span className="flex items-center gap-1"><Target className="w-3 h-3" />{module.quiz_count} quiz Qs</span>
          {score !== undefined && score !== null && <span className="flex items-center gap-1 font-medium text-indigo-600"><Star className="w-3 h-3" />{score}%</span>}
        </div>
        {module.is_mandatory_onboarding && <Badge className="mt-2 bg-red-50 text-red-700 border-red-200 text-xs" variant="outline">Mandatory</Badge>}
      </CardContent>
    </Card>
  );
};

// ─── Compliance Matrix ────────────────────────────────────────
const ComplianceMatrix = () => {
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/training/compliance-matrix')
      .then(res => setMatrix(res.data))
      .catch(() => toast.error('Failed to load compliance matrix'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-8 text-muted-foreground">Loading compliance matrix...</div>;
  if (!matrix) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Shield className="w-5 h-5 text-indigo-600" />Training-to-Framework Compliance Matrix</CardTitle>
        <CardDescription>{matrix.frameworks_covered} frameworks covered by {matrix.total_modules} training modules</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="border-b bg-muted/50">
              <th className="text-left px-3 py-2 font-medium">Module</th>
              <th className="text-left px-3 py-2 font-medium">Frameworks Covered</th>
            </tr></thead>
            <tbody className="divide-y">
              {matrix.modules?.map((m) => (
                <tr key={m.code} className="hover:bg-muted/30">
                  <td className="px-3 py-2 font-medium whitespace-nowrap">{m.code} - {m.title}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {m.frameworks?.map(fw => (
                        <span key={fw} className="px-1.5 py-0.5 bg-indigo-50 text-indigo-600 border border-indigo-100 rounded text-[10px]">{fw}</span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Module Viewer ────────────────────────────────────────────
export const TrainingModuleViewer = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const { isSuperAdmin, userRole } = useTenant();
  const localUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdminUser = isSuperAdmin || userRole === 'admin' || localUser.is_super_admin === true || localUser.role === 'admin';

  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState([]);
  const [quizResult, setQuizResult] = useState(null);
  const [submittingQuiz, setSubmittingQuiz] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => { loadModule(); }, [code, previewMode]);

  const loadModule = async () => {
    try {
      await api.post(`/training/modules/${code}/start`);
      const url = previewMode ? `/training/modules/${code}?preview=true` : `/training/modules/${code}`;
      const res = await api.get(url);
      setModule(res.data);
      setQuizAnswers(new Array(res.data.quiz_questions?.length || 0).fill(-1));
      setQuizResult(null);
    } catch (err) {
      toast.error('Failed to load module');
    } finally { setLoading(false); }
  };

  const togglePreview = () => {
    setPreviewMode(prev => !prev);
    setShowQuiz(false);
    setQuizResult(null);
    setCurrentSlide(0);
  };

  const handleSlideNav = async (dir) => {
    const newSlide = currentSlide + dir;
    if (newSlide >= 0 && newSlide < (module?.slides?.length || 0)) {
      setCurrentSlide(newSlide);
      const pct = Math.round(((newSlide + 1) / module.slides.length) * 100);
      try { await api.post(`/training/modules/${code}/progress`, { progress_pct: pct }); } catch {}
    }
  };

  const handleQuizSubmit = async () => {
    if (quizAnswers.includes(-1)) { toast.error('Please answer all questions'); return; }
    setSubmittingQuiz(true);
    try {
      const res = await api.post(`/training/modules/${code}/quiz`, { answers: quizAnswers });
      setQuizResult(res.data);
      if (res.data.passed) toast.success(`Passed with ${res.data.score}%!`);
      else toast.error(`Score: ${res.data.score}%. Need ${res.data.pass_score}% to pass.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit quiz');
    } finally { setSubmittingQuiz(false); }
  };

  if (loading) return <div className="p-6"><div className="animate-pulse"><div className="h-8 bg-gray-200 rounded w-1/2 mb-4"></div><div className="h-64 bg-gray-200 rounded"></div></div></div>;
  if (!module) return <div className="p-6 text-center">Module not found</div>;

  const isPreviewActive = module.is_preview_mode === true;
  const slide = module.slides?.[currentSlide];
  const slideTypeStyles = {
    info: 'bg-blue-50 border-blue-200',
    warning: 'bg-amber-50 border-amber-200',
    action: 'bg-emerald-50 border-emerald-200',
    case_study: 'bg-purple-50 border-purple-200',
  };

  return (
    <div className="p-6 space-y-4 max-w-4xl mx-auto" data-testid="module-viewer">
      <div className="flex items-center justify-between mb-2">
        <Button variant="ghost" onClick={() => navigate('/grc/training')}><ArrowLeft className="w-4 h-4 mr-1" />Back to Training</Button>
        {isAdminUser && (
          <Button
            variant={previewMode ? 'default' : 'outline'}
            size="sm"
            onClick={togglePreview}
            className={previewMode ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'border-amber-400 text-amber-600 hover:bg-amber-50'}
            data-testid="preview-mode-toggle"
          >
            {previewMode ? <EyeOff className="w-4 h-4 mr-1.5" /> : <Eye className="w-4 h-4 mr-1.5" />}
            {previewMode ? 'Exit Preview' : 'Admin Preview'}
          </Button>
        )}
      </div>

      {/* Preview Mode Banner */}
      {isPreviewActive && (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-800" data-testid="preview-mode-banner">
          <Eye className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm font-medium">Preview Mode — Correct answers are highlighted in the quiz below</span>
        </div>
      )}

      <div className="flex items-center gap-3 mb-2">
        <Badge variant="outline" className={`text-xs ${CATEGORY_COLORS[module.category]}`}>{CATEGORY_LABELS[module.category]}</Badge>
        <Badge variant="outline" className={`text-xs ${DIFF_COLORS[module.difficulty]}`}>{module.difficulty}</Badge>
        <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" />{module.duration_minutes}min</span>
      </div>
      <h1 className="text-xl font-bold" data-testid="module-title">{module.title}</h1>
      <p className="text-sm text-muted-foreground">{module.description}</p>

      {/* Framework Mappings */}
      <div className="flex flex-wrap gap-1">
        {Object.keys(module.framework_mappings || {}).map(fw => (
          <span key={fw} className="px-1.5 py-0.5 bg-indigo-50 text-indigo-600 border border-indigo-100 rounded text-[10px]">{fw}</span>
        ))}
      </div>

      {/* Admin PDF Upload Control */}
      {isPreviewActive && (
        <PdfUploadControl code={code} pdfSlides={module.pdf_slides} onPdfChange={loadModule} />
      )}

      {!showQuiz ? (
        <>
          {/* Slide Viewer - PDF or Text */}
          {module.pdf_slides ? (
            <>
              <PdfSlideViewer code={code} pdfSlides={module.pdf_slides} isAdmin={isAdminUser} onPdfChange={loadModule} />
              <div className="flex justify-end">
                <Button onClick={() => setShowQuiz(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="start-quiz-btn">
                  <Target className="w-4 h-4 mr-1" />
                  {isPreviewActive ? 'Preview Quiz' : `Take Quiz (${module.quiz_questions?.length} questions)`}
                </Button>
              </div>
            </>
          ) : (
            <>
              <Card className={`border-2 ${slideTypeStyles[slide?.type] || 'border-gray-200'}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{slide?.title}</CardTitle>
                    <span className="text-xs text-muted-foreground">Slide {currentSlide + 1} / {module.slides?.length}</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="whitespace-pre-wrap text-sm text-gray-700 leading-relaxed" data-testid="slide-content">{slide?.content}</div>
                </CardContent>
              </Card>
              <Progress value={((currentSlide + 1) / (module.slides?.length || 1)) * 100} className="h-1.5" />
              <div className="flex items-center justify-between">
                <Button variant="outline" disabled={currentSlide === 0} onClick={() => handleSlideNav(-1)} data-testid="prev-slide"><ArrowLeft className="w-4 h-4 mr-1" />Previous</Button>
                {currentSlide === (module.slides?.length || 0) - 1 ? (
                  <Button onClick={() => setShowQuiz(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="start-quiz-btn">
                    <Target className="w-4 h-4 mr-1" />
                    {isPreviewActive ? 'Preview Quiz' : `Take Quiz (${module.quiz_questions?.length} questions)`}
                  </Button>
                ) : (
                  <Button onClick={() => handleSlideNav(1)} data-testid="next-slide">Next<ArrowRight className="w-4 h-4 ml-1" /></Button>
                )}
              </div>
            </>
          )}
        </>
      ) : quizResult ? (
        /* Quiz Results */
        <Card>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${quizResult.passed ? 'text-emerald-700' : 'text-red-700'}`}>
              {quizResult.passed ? <CheckCircle2 className="w-6 h-6" /> : <AlertTriangle className="w-6 h-6" />}
              {quizResult.passed ? 'Quiz Passed!' : 'Quiz Not Passed'}
            </CardTitle>
            <CardDescription>Score: {quizResult.score}% ({quizResult.correct_count}/{quizResult.total_questions}) — Pass mark: {quizResult.pass_score}%</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {quizResult.badges_earned?.length > 0 && (
              <div className="flex gap-2 mb-3">
                {quizResult.badges_earned.map((b, i) => (
                  <Badge key={i} className="bg-amber-100 text-amber-700 border-amber-300 gap-1"><Award className="w-3 h-3" />{b.badge_name}</Badge>
                ))}
              </div>
            )}
            {quizResult.results?.map((r, i) => (
              <div key={i} className={`p-3 rounded-lg border ${r.is_correct ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`} data-testid={`quiz-result-${i}`}>
                <p className="text-sm font-medium mb-1">Q{i+1}: {r.question}</p>
                {!r.is_correct && <p className="text-xs text-red-600 mb-1">Your answer: {module.quiz_questions?.[i]?.options?.[r.selected]}</p>}
                <p className="text-xs text-muted-foreground">{r.explanation}</p>
              </div>
            ))}
            <div className="flex gap-2 pt-3">
              <Button variant="outline" onClick={() => navigate('/grc/training')}>Back to Dashboard</Button>
              {!quizResult.passed && quizResult.attempts_used < quizResult.max_attempts && (
                <Button onClick={() => { setQuizResult(null); setQuizAnswers(new Array(module.quiz_questions?.length || 0).fill(-1)); }}>
                  Retry Quiz ({quizResult.max_attempts - quizResult.attempts_used} attempts left)
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        /* Quiz Form */
        <Card className={isPreviewActive ? 'border-amber-200 bg-amber-50/30' : ''}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-indigo-600" />
              {isPreviewActive ? 'Quiz Preview (Answer Key)' : 'Knowledge Quiz'}
            </CardTitle>
            <CardDescription>
              {isPreviewActive
                ? 'Correct answers are highlighted with a green border. This is a read-only preview for content review.'
                : `Answer all questions. You need ${module.quiz_pass_score || 80}% to pass.`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {module.quiz_questions?.map((q, qi) => (
              <div key={qi} className="p-4 border rounded-lg bg-white" data-testid={`quiz-q-${qi}`}>
                <p className="font-medium text-sm mb-3">Q{qi+1}: {q.question}</p>
                <div className="space-y-2">
                  {q.options?.map((opt, oi) => {
                    const isCorrectAnswer = isPreviewActive && q.correct_index === oi;
                    const isSelected = quizAnswers[qi] === oi;
                    let borderClass = 'border-gray-200 hover:border-gray-300';
                    if (isCorrectAnswer) {
                      borderClass = 'border-emerald-500 bg-emerald-50 ring-1 ring-emerald-300';
                    } else if (isSelected) {
                      borderClass = 'border-indigo-500 bg-indigo-50';
                    }
                    return (
                      <label
                        key={oi}
                        className={`flex items-center gap-3 p-2.5 rounded-lg border transition-colors ${isPreviewActive ? '' : 'cursor-pointer'} ${borderClass}`}
                        data-testid={`quiz-q-${qi}-opt-${oi}`}
                      >
                        {!isPreviewActive && (
                          <input type="radio" name={`q-${qi}`} checked={isSelected} onChange={() => { const a = [...quizAnswers]; a[qi] = oi; setQuizAnswers(a); }} className="accent-indigo-600" />
                        )}
                        {isCorrectAnswer && <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />}
                        <span className={`text-sm ${isCorrectAnswer ? 'font-semibold text-emerald-800' : ''}`}>{opt}</span>
                        {isCorrectAnswer && (
                          <Badge className="ml-auto bg-emerald-100 text-emerald-700 border-emerald-300 text-xs" variant="outline" data-testid={`correct-badge-${qi}`}>Correct</Badge>
                        )}
                      </label>
                    );
                  })}
                </div>
                {isPreviewActive && q.explanation && (
                  <div className="mt-2 p-2 bg-slate-50 rounded border border-slate-200 text-xs text-slate-600" data-testid={`explanation-${qi}`}>
                    <span className="font-medium">Explanation:</span> {q.explanation}
                  </div>
                )}
              </div>
            ))}
            <div className="flex items-center justify-between pt-2">
              <Button variant="outline" onClick={() => setShowQuiz(false)}>Back to Slides</Button>
              {!isPreviewActive && (
                <Button onClick={handleQuizSubmit} disabled={submittingQuiz || quizAnswers.includes(-1)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-quiz-btn">
                  {submittingQuiz ? 'Submitting...' : `Submit Quiz (${quizAnswers.filter(a => a !== -1).length}/${module.quiz_questions?.length})`}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TrainingDashboard;
